/**
 * Email Service
 * Handles email sending and syncing via Slope server (which uses Microsoft Graph)
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

export interface EmailAttachment {
  name: string;
  contentType: string;
  contentBytes: string; // Base64 encoded
  size?: number;
}

export interface SendEmailRequest {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  body: string;
  bodyType?: 'text' | 'html';
  attachments?: EmailAttachment[];
  organizationId: string;
  contactId?: string;
}

export interface SendEmailResponse {
  success: boolean;
  messageId?: string;
  conversationId?: string;
  error?: string;
}

export interface EmailSyncResponse {
  success: boolean;
  conversationsSynced: number;
  newMessagesFound: number;
  activitiesCreated: number;
  error?: string;
}

class EmailService {
  /**
   * Get session ID from localStorage
   */
  private getSessionId(): string | null {
    return localStorage.getItem('sessionId');
  }

  /**
   * Send an email via Exchange Online
   */
  async sendEmail(request: SendEmailRequest): Promise<SendEmailResponse> {
    const sessionId = this.getSessionId();
    if (!sessionId) {
      return {
        success: false,
        error: 'Not authenticated',
      };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/email/v1/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': sessionId,
        },
        body: JSON.stringify(request),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || 'Failed to send email',
        };
      }

      return {
        success: true,
        messageId: data.messageId,
        conversationId: data.conversationId,
      };
    } catch (error) {
      console.error('Failed to send email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send email',
      };
    }
  }

  /**
   * Sync email replies from Exchange Online
   * Called on user login as a background task
   */
  async syncEmails(): Promise<EmailSyncResponse> {
    const sessionId = this.getSessionId();
    if (!sessionId) {
      return {
        success: false,
        conversationsSynced: 0,
        newMessagesFound: 0,
        activitiesCreated: 0,
        error: 'Not authenticated',
      };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/email/v1/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': sessionId,
        },
        body: JSON.stringify({}),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          conversationsSynced: 0,
          newMessagesFound: 0,
          activitiesCreated: 0,
          error: data.error || 'Failed to sync emails',
        };
      }

      return {
        success: true,
        conversationsSynced: data.conversationsSynced,
        newMessagesFound: data.newMessagesFound,
        activitiesCreated: data.activitiesCreated,
      };
    } catch (error) {
      console.error('Failed to sync emails:', error);
      return {
        success: false,
        conversationsSynced: 0,
        newMessagesFound: 0,
        activitiesCreated: 0,
        error: error instanceof Error ? error.message : 'Failed to sync emails',
      };
    }
  }

  /**
   * Convert a File object to base64 for attachment
   */
  async fileToAttachment(file: File): Promise<EmailAttachment> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = () => {
        const base64 = reader.result as string;
        // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
        const contentBytes = base64.split(',')[1];
        
        resolve({
          name: file.name,
          contentType: file.type || 'application/octet-stream',
          contentBytes,
          size: file.size,
        });
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsDataURL(file);
    });
  }

  /**
   * Convert multiple files to attachments
   */
  async filesToAttachments(files: File[]): Promise<EmailAttachment[]> {
    return Promise.all(files.map(file => this.fileToAttachment(file)));
  }
}

export const emailService = new EmailService();
